import React, { useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Printer, ArrowLeft, Award, TrendingUp, Users } from "lucide-react";
import { MEMBERS, ROUNDS, SEATING, findMember, INITIAL_REFERRALS } from "@/data/seed";
import { useDemo } from "@/store/DemoStore";
import { RealQR } from "@/components/RealQR";

export default function Recap() {
  const { id } = useParams();
  const nav = useNavigate();
  const { state } = useDemo();

  // Registered member takes precedence when the URL id matches; else fall back to seeded member
  const member = useMemo(() => {
    if (state.registeredMember && state.registeredMember.id === id) return state.registeredMember;
    return findMember(id) || MEMBERS[0];
  }, [id, state.registeredMember]);

  const referrals = state.referrals || INITIAL_REFERRALS;

  const given    = referrals.filter(r => r.from === member.id);
  const received = referrals.filter(r => r.to   === member.id);
  const givenCount    = given.reduce((a, r) => a + r.count, 0);
  const receivedCount = received.reduce((a, r) => a + r.count, 0);
  const givenValue    = given.reduce((a, r) => a + r.value * r.count, 0);
  const receivedValue = received.reduce((a, r) => a + r.value * r.count, 0);

  // Table history — stable table derivation for registered members not in SEATING
  const stableTable = useMemo(() => {
    const s = String(member.id); let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return (h % 50) + 1;
  }, [member.id]);
  const history = ROUNDS.map((r) => {
    const t = SEATING[r.id].find((tb) => tb.seats.some(s => s.memberId === member.id));
    const tableNo = t?.tableNo || stableTable;
    const tSeats  = t?.seats || [];
    const partners = tSeats
      .filter(s => s.memberId !== member.id)
      .map(s => findMember(s.memberId))
      .filter(Boolean);
    return { round: r, table: tableNo, partners };
  });

  const autoPrint = new URLSearchParams(window.location.search).get("print") === "1";
  useEffect(() => {
    if (autoPrint) {
      const t = setTimeout(() => window.print(), 700);
      return () => clearTimeout(t);
    }
  }, [autoPrint]);

  const displayName = member.isStall ? (member.stallName || member.name) : member.name;

  return (
    <div className="recap-root min-h-screen">
      {/* On-screen toolbar (hidden on print) */}
      <div className="no-print sticky top-0 z-10 hairline-b" style={{ background: "var(--bg)" }}>
        <div className="max-w-[900px] mx-auto px-6 py-3 flex items-center justify-between">
          <button data-testid="recap-back-btn" onClick={() => nav(-1)} className="btn-ghost !py-2">
            <ArrowLeft size={14} /> Back
          </button>
          <div className="mono-label" style={{ color: "var(--taupe)" }}>Personal Recap · Auto-formatted for A4</div>
          <button data-testid="recap-print-btn" onClick={() => window.print()} className="btn-red !py-2 !px-4">
            <Printer size={14} /> Print / Save as PDF
          </button>
        </div>
      </div>

      {/* Recap page — designed to fit one A4 sheet */}
      <div className="recap-sheet">
        <header className="flex items-start justify-between hairline-b pb-5">
          <div>
            <div className="eyebrow">BNI Davangere Region · Members Day 2026</div>
            <div className="font-serif text-3xl mt-3">Personal Recap</div>
            <div className="mono-label mt-2" style={{ color: "var(--taupe)" }}>Trishul Kalaabhavana · 28 Nov 2026</div>
          </div>
          <div className="text-right">
            <RealQR regId={member.regId || member.id} size={92} />
            <div className="mono-label mt-2" style={{ color: "var(--taupe)" }}>{member.id}</div>
          </div>
        </header>

        <section className="mt-6 grid grid-cols-3 gap-5">
          <div className="col-span-2">
            <div className="mono-label">Attendee</div>
            <div className="font-serif text-2xl mt-1">{displayName}</div>
            <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}>
              {member.isStall
                ? (member.contactPerson ? `Contact · ${member.contactPerson}` : "Business exhibitor")
                : `${member.chapter} · ${member.region}`}
            </div>
            <div className="mt-2 text-sm" style={{ color: "var(--body)" }}>{member.category}</div>
          </div>
          <div className="text-right">
            <div className="mono-label">Pass Tier</div>
            <div className="font-serif text-lg mt-1">{member.tierName || member.tier}</div>
          </div>
        </section>

        {/* Big numbers */}
        <section className="mt-6 grid grid-cols-4 gap-0 hairline">
          <Stat icon={TrendingUp} label="Referrals Given"    v={givenCount} sub={`₹${(givenValue/1000).toFixed(0)}K`} accent="red" />
          <Stat icon={Award}      label="Referrals Received" v={receivedCount} sub={`₹${(receivedValue/1000).toFixed(0)}K`} accent="green" />
          <Stat icon={Users}      label="Tables Seated"      v={history.length} sub="4 conclave rounds" />
          <Stat icon={TrendingUp} label="Net Business"       v={`₹${((givenValue+receivedValue)/100000).toFixed(1)}L`} sub="influenced volume" accent="red" />
        </section>

        {/* Table history */}
        <section className="mt-6">
          <div className="mono-label">Your Conclave · Round by Round</div>
          <div className="mt-3 grid grid-cols-2 gap-4">
            {history.map((h) => (
              <div key={h.round.id} className="hairline p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-serif text-lg">{h.round.label}</div>
                    <div className="mono-label" style={{ color: "var(--taupe)" }}>{h.round.time}</div>
                  </div>
                  <div className="text-right">
                    <div className="mono-label">Table</div>
                    <div className="font-serif text-2xl">#{h.table}</div>
                  </div>
                </div>
                {h.partners.length > 0 && (
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    {h.partners.slice(0, 5).map(p => (
                      <span key={p.id} className="text-xs" style={{ color: "var(--body)" }}>
                        {p.name}<span style={{color:"var(--taupe)"}}> · {p.chapter}</span>
                      </span>
                    )).reduce((prev, curr, i) => i === 0 ? [curr] : [...prev, <span key={"sep"+i} style={{color:"var(--muted)"}}>·</span>, curr], [])}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Referrals given & received */}
        <section className="mt-6 grid grid-cols-2 gap-5">
          <div>
            <div className="mono-label">Referrals You Gave</div>
            <div className="mt-3">
              {given.length === 0 && <div className="mono-label" style={{color:"var(--muted)"}}>—</div>}
              {given.slice(0, 6).map((r) => {
                const to = findMember(r.to);
                return (
                  <div key={r.id} className="hairline-b py-2 flex items-center justify-between text-xs">
                    <span>→ {to?.name || "—"} <span style={{color:"var(--taupe)"}}>· R{r.round} T#{r.table}</span></span>
                    <span className="mono-label" style={{ color: "var(--red)" }}>+{r.count} · ₹{(r.value/1000).toFixed(0)}k</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div>
            <div className="mono-label">Referrals You Received</div>
            <div className="mt-3">
              {received.length === 0 && <div className="mono-label" style={{color:"var(--muted)"}}>—</div>}
              {received.slice(0, 6).map((r) => {
                const from = findMember(r.from);
                return (
                  <div key={r.id} className="hairline-b py-2 flex items-center justify-between text-xs">
                    <span>← {from?.name || "—"} <span style={{color:"var(--taupe)"}}>· R{r.round} T#{r.table}</span></span>
                    <span className="mono-label" style={{ color: "var(--green)" }}>+{r.count} · ₹{(r.value/1000).toFixed(0)}k</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <footer className="mt-8 pt-4 hairline-t flex items-center justify-between mono-label" style={{ color: "var(--taupe)" }}>
          <span>bni-davangere.in · Members Day 2026</span>
          <span>Generated {new Date().toLocaleDateString("en-IN")}</span>
        </footer>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, v, sub, accent }) {
  const c = accent === "red" ? "var(--red-2)" : accent === "green" ? "var(--green)" : "var(--text)";
  return (
    <div className="p-4 border-r border-[var(--hairline)] last:border-r-0">
      <div className="flex items-center gap-2">
        <Icon size={12} style={{ color: "var(--taupe)" }} />
        <div className="mono-label">{label}</div>
      </div>
      <div className="font-serif text-3xl mt-2 recap-stat-value" style={{ color: c }}>{v}</div>
      {sub && <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}>{sub}</div>}
    </div>
  );
}
